/**
 * yepairag 日历存储的浏览器客户端(2026-08-24,配 yepairag PR #1206)。
 *
 * 走 apiClient 的 yepairag base(Kong 把 /yepairag/api/* 重写到 /yepairag/*,
 * 与 /yepairag/operation/product-sales 同一条已在生产使用的通路)。
 *
 * 契约(后端唯一的写接口):POST /merchant/marketing/calendar/item —
 * "这条内容什么时候发"。date=null 即退回 Content Pool。
 * 为什么是 POST 不是 PATCH:dev Kong 的 /yepairag 路由只放行 GET/POST —— PATCH 在 Kong 层
 * 就 404("no Route matched")且不带 CORS 头,浏览器只看到 status 0(2026-08-25 实测)。
 * 后端同路径兼收 PATCH,仅供脚本/curl 用。
 */
import { apiClient } from '@lib/core/api';
import { envelopeMessage, isErrorEnvelope } from '@lib/core/errorEnvelope';

export interface StoreCalendarVersion {
  platform: string;
  /** agent 的 draft_id(= chatbot-api 的 clientDraftId),三源去重的键。 */
  draftId?: string | null;
  title: string;
  body: string;
  imageUrl?: string | null;
  status: 'ok' | 'failed';
  failReason?: string | null;
  /** 'generate' | 'publish' —— 决定 Retry 是重跑 writer 还是重发渠道(PRD #16 ②③)。 */
  failStage?: string | null;
  /**
   * [3.5 #3] 原帖地址。写入端一直在发(recordItemPublished / recordDraftPublished 的
   * channels[].external_url),读取端却从没把它带上来 —— 于是日历上已发布的那一格
   * 拿不到链接,「View post」时有时无(验收 #20 说的就是 Facebook 这个现象)。
   *
   * 后端这个字段的命名还没最终对齐,所以两种拼法都收(见 useCalendarContent 的映射)。
   * 真的没有时不是灾难:PRD 要求那种情况下按钮置灰并注明原因,而不是不显示。
   */
  externalUrl?: string | null;
  external_url?: string | null;
}

export interface StoreCalendarItem {
  id: string;
  /** 批次身份：一次生成任务的所有条目共享同一个 plan_id。null = 不属于任何批次。 */
  planId?: string | null;
  /** 批次名（marketing.plans.label），批次分组的组标题。 */
  planLabel?: string | null;
  title: string;
  date: string | null;
  time: string;
  status: string;
  genCount: number;
  /** 这一行最后一次写进内容的时刻（ISO/UTC）——内容池的排序键。 */
  contentAt?: string | null;
  /**
   * 篇目在批次里的位置（0 起）。一个计划是在一个事务里提交的，所有行的 createdAt
   * 一模一样，所以这是唯一能还原「计划里的第几篇」的东西。老批次没有 → null。
   */
  planSeq?: number | null;
  /** 行自己的创建时刻（ISO/UTC）；批次组内按它升序 = 计划里的篇目顺序。 */
  createdAt?: string | null;
  setup: { label: string; products: number; keywords: number };
  campaign?: { code: string; label: string } | null;
  channels: string[];
  versions: StoreCalendarVersion[];
}

export interface StoreCalendarMonth {
  month: string;
  items: StoreCalendarItem[];
  pool: StoreCalendarItem[];
  /**
   * 每一篇「已经有日期」的草稿 id —— 含本月之外的月份。
   * items 只装本月,所以没有这份名单时前端分不清「这篇没排期」和「这篇排在别的月」,
   * 会把九月的已排期稿当散稿并回八月的内容池(实测同一秒 8 月 94 / 9 月 78)。
   */
  scheduledDraftIds?: string[];
  /**
   * 存储源认领过的**全部**草稿 id —— 不管有没有日期,也不管是不是被改稿换掉的旧版。
   * 比 scheduledDraftIds 多的正是「被改稿顶掉的旧 draft_id」:日历行只留最新那版,
   * 而 chatbot-api 的草稿是一版一行、旧行永远 DRAFT —— 少了这份名单,每改一次稿
   * 就在内容池里多一张假卡(拖不动,因为排期接口只收 cal_ 行;Clear pool 还会删掉它)。
   */
  knownDraftIds?: string[];
}

const BASE = '/merchant/marketing/calendar';

export async function fetchCalendarStoreMonth(
  _tenantId: string,
  month: string
): Promise<StoreCalendarMonth> {
  // 整月读会顺带触发内容认知(调模型),所以走 chatbot 代理:商家身份取自 JWT、带商家 vkey 计费。
  // tenant_id 不再传 —— 代理只认 JWT;参数留着是为了不动调用方。
  const response = await apiClient.chatbotGet<StoreCalendarMonth>(
    '/digital-staff/marketing-calendar',
    { month },
    // The client default is 10 s. Measured on dev 2026-09-08: this read answered 200 on the
    // server and still failed in the browser under load, and a failed store read empties the
    // Content Pool over real items (the pool is drawn from this response).
    { timeout: 60_000 }
  );
  // 全局 axios 拦截器把非 2xx（存储未部署时是 503）包成伪 200 的 {errorCode, errorMessage} resolve 掉。
  // 不在这里升回异常，调用方拿到的就是个没有 items/pool 的畸形对象，
  // mergeCalendarSources 展开 undefined 直接抛 → 明明成功的草稿/发布两源也被一起打空
  // （表现为「Couldn't load your content just now」+ 空池）。抛出去，让上层 .catch(() => null) 正常回落。
  const d = response.data as unknown;
  if (d && typeof d === 'object' && 'errorCode' in (d as Record<string, unknown>)) {
    throw new Error(
      String((d as { errorMessage?: unknown }).errorMessage ?? 'calendar store unavailable')
    );
  }
  return response.data;
}

/**
 * One scheduled row by id, for a caller that knows the id and not the date.
 *
 * The Task Centre's recovery panel is that caller: a card names the piece that
 * failed and carries no date, because a cause is not a date. The month read
 * cannot answer it — and not just awkwardly: a month is ~770 KB–2 MB because
 * the pool carries every version's full body (dev 2026-09-18: 2 MB in 9–37 s),
 * and three months fetched together came back through the gateway as 502.
 *
 * `null` when the row is gone — deleted, or another tenant's. The panel turns
 * that into 「this piece is no longer here」 rather than an empty editor.
 */
export async function fetchCalendarItem(
  tenantId: string,
  itemId: string
): Promise<StoreCalendarItem | null> {
  try {
    const response = await apiClient.yepairagGet<{ item: StoreCalendarItem }>(`${BASE}/item`, {
      tenant_id: tenantId,
      item_id: itemId,
    });
    const d = response.data as unknown;
    // 同 fetchCalendarStoreMonth：全局拦截器把非 2xx 包成伪 200 的错误信封 resolve 掉，
    // 不认出来的话调用方会拿着一个没有 item 的对象往下走。
    if (!d || typeof d !== 'object' || 'errorCode' in (d as Record<string, unknown>)) {
      return null;
    }
    return (d as { item?: StoreCalendarItem }).item ?? null;
  } catch {
    return null;
  }
}

export async function rescheduleCalendarItem(
  tenantId: string,
  itemId: string,
  date: string | null,
  time: string | null
): Promise<void> {
  const res = await apiClient.yepairagPost(`${BASE}/item`, {
    tenant_id: tenantId,
    item_id: itemId,
    date,
    time,
  });
  // 全局 axios 拦截器把非 2xx 包成伪 200 的 {errorCode, errorMessage} resolve 掉 ——
  // 这里必须升回异常,否则调用方的乐观更新永远不回滚(拖拽失败会假装成功)。
  const d = (res as { data?: unknown } | undefined)?.data;
  if (d && typeof d === 'object' && 'errorCode' in d) {
    throw new Error(String((d as { errorMessage?: unknown }).errorMessage ?? 'reschedule failed'));
  }
}

/** [PRD #16 Retry · 2026-08-28] 开一张重做票。
 *
 *  Retry 的两件事实 —— 重做哪一行、哪个渠道 —— 前端本来就握着,却曾经只能写进
 *  给模型的句子里(实测:重做 IG 版回来一篇 blog,还从日历上消失)。这个调用把它们
 *  冻在服务端,消息里只带一个不可解释的 token;渠道/标题/落位由服务端从票里读回。
 *  失败要抛:开不出票就不该假装重做已经在路上。 */
/** 一次批量的逐篇结果 —— 完成卡的数据源(3.5 #9)。 */
export interface PlanOutcomePiece {
  itemId: string;
  title: string;
  date: string | null;
  time: string;
  channel: string;
  status: 'ok' | 'failed';
  failReason: string | null;
  /** 'generate' = 压根没写出来(重试 = 重跑 writer);'publish' = 写出来了没发出去。 */
  failStage: 'generate' | 'publish' | null;
  draftId: string | null;
}

export interface PlanOutcome {
  planId: string;
  planLabel: string;
  expected: number;
  succeeded: number;
  failed: number;
  pieces: PlanOutcomePiece[];
}

/**
 * 这一批到底怎么样了 —— 按 plan_id 问,不按月问。
 *
 * 完成卡以前只会做减法(expected - generated),所以说得出「2 篇失败」却说不出**哪两篇**,
 * 「失败篇目单条重试」没有东西可重试。这个接口把逐篇成败从库里读回来:行在提交时声明了
 * channels,version 只有真的落了稿才建,declared - present 就是没生成出来的那些 —— 不经模型。
 *
 * 按 plan_id 而不是按月:一个批次可能跨月(九月的计划里排了十月的槽),按月拉会漏。
 */
export async function fetchPlanOutcome(
  tenantId: string,
  planId: string
): Promise<PlanOutcome | null> {
  try {
    const response = await apiClient.yepairagGet<PlanOutcome>(
      `${BASE}/plan/${encodeURIComponent(planId)}/outcome`,
      { tenant_id: tenantId }
    );
    const d = response.data as unknown;
    // 全局拦截器把非 2xx 包成伪 200 的错误信封 —— 别把它当成一份「零篇」的结果。
    if (!d || typeof d !== 'object' || 'errorCode' in (d as Record<string, unknown>)) return null;
    const out = d as PlanOutcome;
    return Array.isArray(out.pieces) ? out : null;
  } catch {
    // 拿不到就让调用方退回旧的减法口径,而不是把完成卡整张打空。
    return null;
  }
}

export async function openRedoTicket(
  tenantId: string,
  itemId: string,
  channel: string,
  extra: {
    failReason?: string | null;
    note?: string | null;
    timezone?: string | null;
    /** 这次重做属于哪段对话 —— 服务端凭它认领这张票,票号不再经模型的手。 */
    conversationId?: string | null;
  } = {}
): Promise<{
  token: string;
  channel: string;
  title?: string | null;
  scheduled_date?: string | null;
  fail_reason?: string | null;
}> {
  const res = await apiClient.yepairagPost(`${BASE}/item/redo-ticket`, {
    tenant_id: tenantId,
    item_id: itemId,
    channel,
    fail_reason: extra.failReason ?? null,
    note: extra.note ?? null,
    timezone: extra.timezone ?? null,
    conversation_id: extra.conversationId ?? null,
  });
  const d = (res as { data?: unknown } | undefined)?.data as
    (Record<string, unknown> & { errorCode?: number; errorMessage?: string }) | undefined;
  if (!d || 'errorCode' in d || typeof d.token !== 'string') {
    throw new Error(String(d?.errorMessage ?? 'could not open a redo ticket'));
  }
  return d as unknown as { token: string; channel: string };
}

/** 同一张票,但只报得出 draft_id 的入口用(chat 草稿卡的 Regenerate、弹窗的
 *  Regenerate in chat)。行和渠道由**服务端**按草稿反查,不写进消息、不经过模型。
 *
 *  草稿从没落到任何一行(或已被丢弃)时服务端回 404 —— 这里返回 null 而不是抛,
 *  调用方据此退回原来那条纯文字的老路,而不是把改稿整个拦下来。 */
export async function openRedoTicketForDraft(
  tenantId: string,
  draftId: string,
  extra: { note?: string | null; timezone?: string | null; conversationId?: string | null } = {}
): Promise<{ token: string; channel: string; title?: string | null } | null> {
  const res = await apiClient.yepairagPost(`${BASE}/item/redo-ticket`, {
    tenant_id: tenantId,
    draft_id: draftId,
    note: extra.note ?? null,
    timezone: extra.timezone ?? null,
    // 服务端凭这段对话认领这张票 —— 票号不进消息、不经模型。
    conversation_id: extra.conversationId ?? null,
  });
  const d = (res as { data?: unknown } | undefined)?.data as
    (Record<string, unknown> & { errorCode?: number }) | undefined;
  if (!d || 'errorCode' in d || typeof d.token !== 'string') return null;
  return d as unknown as { token: string; channel: string; title?: string | null };
}

/** [PRD #8 · 2026-08-26] Discard 整组:条目+全部版本软删(status='deleted')。
 *  失败必须抛 —— 调用方只有拿到真 200 才把卡片从本地拿掉,否则刷新即复活
 *  (这个按钮此前就是纯本地过滤的假象)。 */
export async function discardCalendarItem(tenantId: string, itemId: string): Promise<void> {
  const res = await apiClient.yepairagPost(`${BASE}/item/discard`, {
    tenant_id: tenantId,
    item_id: itemId,
  });
  const d = (res as { data?: unknown } | undefined)?.data;
  if (d && typeof d === 'object' && 'errorCode' in d) {
    throw new Error(String((d as { errorMessage?: unknown }).errorMessage ?? 'discard failed'));
  }
}

/** [Publish Now · 2026-08-26] 直发成功后的回填:各渠道 external 身份记到版本行,
 *  条目挪到真实发布时刻(提前发布的 8/27 帖会出现在 8/26,时间=真实发出时间)。
 *  只在 ≥1 渠道成功时调用。 */
export async function recordItemPublished(
  tenantId: string,
  itemId: string,
  publishedDate: string,
  publishedTime: string,
  channels: {
    channel: string;
    draft_id?: string | null;
    title?: string | null;
    external_id?: string | null;
    external_url?: string | null;
  }[]
): Promise<void> {
  const res = await apiClient.yepairagPost(`${BASE}/item/published`, {
    tenant_id: tenantId,
    item_id: itemId,
    published_date: publishedDate,
    published_time: publishedTime,
    channels,
  });
  const d = (res as { data?: unknown } | undefined)?.data;
  if (d && typeof d === 'object' && 'errorCode' in d) {
    throw new Error(
      String((d as { errorMessage?: unknown }).errorMessage ?? 'publish bookkeeping failed')
    );
  }
}

/** [聊天直发 · 2026-08-26] /item/published 的无 item_id 版:聊天草稿不在日历上,
 *  后端按 draft_id 回填条目+版本行并记 external 身份——不调它,聊天发布的帖子
 *  对效果卡和指标拉取都是隐形的。只在 ≥1 渠道成功时调用。 */
export async function recordDraftPublished(
  tenantId: string,
  publishedDate: string,
  publishedTime: string,
  channels: {
    channel: string;
    draft_id?: string | null;
    title?: string | null;
    external_id?: string | null;
    external_url?: string | null;
  }[]
): Promise<void> {
  const res = await apiClient.yepairagPost(`${BASE}/draft-published`, {
    tenant_id: tenantId,
    published_date: publishedDate,
    published_time: publishedTime,
    channels,
  });
  const d = (res as { data?: unknown } | undefined)?.data;
  if (d && typeof d === 'object' && 'errorCode' in d) {
    throw new Error(
      String((d as { errorMessage?: unknown }).errorMessage ?? 'publish bookkeeping failed')
    );
  }
}

/** 每帖最新指标快照(marketing.post_metrics 的 DISTINCT ON 最新行)。
 *  键 = agent draft id(chatbot-api 草稿的 clientDraftId);缺席的 draft 不在返回里。 */
export interface PostMetricsSnapshot {
  channel?: string;
  metric_date?: string;
  views: number | null;
  reach: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  saved: number | null;
  /** 平台自己数的 LINK 点击(Facebook 的 post_clicks_by_type link 桶),不是 GA4 会话。
   *  null = 平台没给过 breakdown(IG 永远如此);0 = 给了,但没人点。别把两者混成一个。 */
  clicks?: number | null;
  /** 折扣码核销数(PRD #17 ③)。**这是「这个码」的转化,不是「这篇帖」的** ——
   *  一个变体码由本批次所有帖子共用,所以同码的几篇会显示同一个数。 */
  conversions?: number | null;
  /** 上述核销带来的订单金额合计。 */
  revenue?: number | null;
  currency?: string | null;
  /** 归因来源,PRD #17 ④ 要求的原话:`code WINTER15-IG`。null = 没有可报的数字。 */
  conversion_source?: string | null;
  /** 数字是从哪个码数出来的,用来告诉商家两篇帖共用一个码。 */
  conversion_code?: string | null;
}

export async function fetchPostMetrics(
  tenantId: string,
  draftIds: string[]
): Promise<Record<string, PostMetricsSnapshot>> {
  if (draftIds.length === 0) return {};
  const response = await apiClient.yepairagGet<{ metrics: Record<string, PostMetricsSnapshot> }>(
    `${BASE}/post-metrics`,
    { tenant_id: tenantId, draft_ids: draftIds.join(',') }
  );
  return response.data?.metrics ?? {};
}

/** 卡片提交时把商家选定的商品(标题/链接/图)直接落到服务端。
 *  以前这些逐条写在给模型的隐藏指令里、要求它 "EXACT" 转抄 —— 抄错的链接是一个
 *  指向不存在页面的 CTA,而且不会报错。建稿时服务端按对话读回这份事实。 */
export async function saveBriefProducts(
  tenantId: string,
  conversationId: string,
  products: {
    id?: string;
    title: string;
    product_url?: string;
    image_url?: string;
    price?: string;
  }[],
  /**
   * [2026-09-09] 关键词**按渠道**,不是拍平的一个列表。
   *
   * 形状 {"blog": [...], "instagram": [...]}。建稿时服务端按正在写的那个渠道读回来,
   * 模型传的 tags 只要不在那份清单里就丢掉 —— 拍平之后谁也说不出哪个词属于哪个渠道,
   * 而那正是 IG 文案里挂着 Shopify blog SEO 词的成因。
   *
   * 省略时服务端保住已存的那一份,不会清空。
   */
  keywordsByChannel?: Record<string, string[]>
): Promise<void> {
  const response = await apiClient.yepairagPost(`${BASE}/brief-products`, {
    tenant_id: tenantId,
    conversation_id: conversationId,
    products,
    ...(keywordsByChannel ? { keywords: keywordsByChannel } : {}),
  });
  // apiClient 把 401/502 以外的 HTTP 错误(含断网)改写成 resolve 的 {errorCode, errorMessage}
  // 信封。调用方要靠「这里抛不抛」决定发不发消息,所以信封必须变回异常 —— 否则存失败也算成功,
  // Maya 建稿时服务端读不到这份选品。
  const body = response?.data as unknown;
  if (isErrorEnvelope(body)) {
    throw new Error(envelopeMessage(body, 'Could not save the selected products'));
  }
}

/** 排期卡确认后的一篇。date/time 是**店铺**墙上时钟(调用方换算好再传)。 */
export interface PlanBriefPost {
  date: string;
  time: string;
  channels: string[];
  /** 拍平口径,保留给不分渠道的读取方(卡片回显、日历上的 setup 标签)。 */
  topic_keywords?: string[];
  /**
   * [2026-09-09] 同一批词**按渠道**的那一份。
   *
   * 拍平之后谁也说不出哪个词属于哪个渠道 —— 那正是 IG 文案里挂着 Shopify blog SEO 词的
   * 成因。服务端拿它算出每个渠道跨整批的并集,写进 content_briefs.keywords,建稿时按
   * 正在写的渠道卡住 tags。
   *
   * 用并集而不是某一行:批量里每篇可能各有覆盖,而建稿工具并不知道自己在写哪一行。
   * 并集恰好只堵跨渠道那道口子 —— blog 的词进不了 IG,而某篇自定义的 IG hashtag
   * 也不会被误删。
   */
  topic_keywords_by_channel?: Record<string, string[]>;
  products?: { id?: string; title: string }[];
  campaign_code?: string | null;
  custom?: boolean;
}

/**
 * 把排期卡确认后的整份配置直写服务端(3.5 #11 + 2026-08-30 铁律)。
 *
 * 以前这份配置是拼成一段话发给 Maya 的 ——「Exact dates (schedule these **verbatim**,
 * all upcoming): Sep 3 19:00, …」—— 再指望她一字不差抄进 CREATE_CONTENT_CALENDAR。
 * 抄错一个钟点就是帖子发在错误的时刻,而且不报错。现在卡片直接写,工具按这段对话读回来。
 *
 * ⚠️ posts 里的 date/time 必须已经是**店铺**墙上时钟:发布器拿 shop.ianaTimezone 解释
 * 它们 —— 而 2026-09-10 起那个「解释用的时区」就是**商家自己的表**,所以这里不换算:
 * 商家选的钟点原样送过去。
 */
export async function savePlanBrief(
  tenantId: string,
  conversationId: string,
  plan: { planLabel: string; posts: PlanBriefPost[]; sharedSetup?: Record<string, unknown> }
): Promise<void> {
  await apiClient.yepairagPost(`${BASE}/plan-brief`, {
    tenant_id: tenantId,
    conversation_id: conversationId,
    plan,
  });
}

/** 商家选的默认 Facebook Page。发到哪个 Page 不该由模型转述 —— 抄错就发到别人的主页。 */
export async function savePublishPref(
  tenantId: string,
  facebookPageId: string | null
): Promise<void> {
  await apiClient.yepairagPost(`${BASE}/publish-prefs`, {
    tenant_id: tenantId,
    facebook_page_id: facebookPageId,
  });
}
